import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  AdminQueryMessage,
  getAdminQueriesByGcaUser,
  sendAdminQuery,
} from "src/utils/api/adminQuery";
import MessageBubble from "./MessageBubble";
import { useAppSelector } from "src/redux/store";

interface Props {
  open: boolean;
  onClose: () => void;
  gcaUserId: string;
}

const QueriesDialog = ({ open, onClose, gcaUserId }: Props) => {
  const queryClient = useQueryClient();
  const role = useAppSelector((state: any) => state.root.user)?.data?.role;
  const isApprover = role === "Approver";
  const [draft, setDraft] = useState("");
  const [errorText, setErrorText] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const queryKey = ["adminQueries", gcaUserId];

  const { data, isLoading, isError } = useQuery(
    queryKey,
    () => getAdminQueriesByGcaUser(gcaUserId),
    {
      enabled: open && !!gcaUserId,
      refetchInterval: open ? 30000 : false,
      onSuccess: () => {
        queryClient.invalidateQueries(["adminQueryUnread", gcaUserId]);
      },
    },
  );

  const messages: AdminQueryMessage[] = data?.data?.data || [];

  const { mutate, isLoading: isSending } = useMutation(
    (message: string) => sendAdminQuery({ receiverId: gcaUserId, message }),
    {
      onSuccess: () => {
        setDraft("");
        setErrorText(null);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (err: any) => {
        setErrorText(
          err?.response?.data?.message || "Failed to send message",
        );
      },
    },
  );

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, open]);

  const handleSend = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    mutate(trimmed);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pr: 1,
        }}
      >
        Queries
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          ref={scrollRef}
          sx={{
            height: 420,
            overflowY: "auto",
            p: 2,
            bgcolor: "background.default",
          }}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : isError ? (
            <Typography variant="body2" sx={{ color: "error.main" }}>
              Failed to load queries.
            </Typography>
          ) : messages.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", textAlign: "center", py: 4 }}
            >
              No queries yet. Send the first message below.
            </Typography>
          ) : (
            messages.map((m) => (
              <MessageBubble
                key={m.id}
                message={m}
                isMine={m.senderId !== gcaUserId}
              />
            ))
          )}
        </Box>
      </DialogContent>
      {isApprover && (
        <DialogActions sx={{ p: 2, flexDirection: "column", alignItems: "stretch" }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", width: "100%" }}>
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              placeholder="Type a query for this GCA..."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              size="small"
              disabled={isSending}
            />
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={isSending || !draft.trim()}
            >
              {isSending ? "Sending..." : "Send"}
            </Button>
          </Box>
          {errorText && (
            <Typography
              variant="caption"
              sx={{ color: "error.main", mt: 1, display: "block" }}
            >
              {errorText}
            </Typography>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default QueriesDialog;
